import {FluentBundle,FluentResource} from '@fluent/bundle';
import english from './locales/en.json' with {type:'json'};

export const languages=[
  {code:'en',name:'English',direction:'ltr'},
  {code:'de',name:'Deutsch',direction:'ltr'},
  {code:'it',name:'Italiano',direction:'ltr'},
  {code:'sv',name:'Svenska',direction:'ltr'},
  {code:'ru',name:'Русский',direction:'ltr'},
  {code:'so',name:'Soomaali',direction:'ltr'},
  {code:'ro',name:'Română',direction:'ltr'},
  {code:'sq',name:'Shqip',direction:'ltr'},
  {code:'nl',name:'Nederlands',direction:'ltr'},
  {code:'pt-br',name:'Português brasileiro',direction:'ltr'},
  {code:'pl',name:'Polski',direction:'ltr'},
  {code:'ko',name:'한국어',direction:'ltr'},
  {code:'uk',name:'Українська',direction:'ltr'},
  {code:'hi-IN',name:'हिन्दी',direction:'ltr'},
  {code:'fr',name:'Français',direction:'ltr'},
  {code:'zh-CN',name:'简体中文',direction:'ltr'},
];
export function editionForLanguage(books,book,language){
  if(!book?.id?.startsWith('easypeasy-'))return null;
  return books.find(item=>item.id.startsWith('easypeasy-')&&item.language.toLowerCase()===language.toLowerCase())??null;
}
export const languageStorageKey='webreader.ui-language';
const loaders={
  en:async()=>english,
  'de':async()=>(await import('./locales/de.json',{with:{type:'json'}})).default,
  'it':async()=>(await import('./locales/it.json',{with:{type:'json'}})).default,
  'sv':async()=>(await import('./locales/sv.json',{with:{type:'json'}})).default,
  'ru':async()=>(await import('./locales/ru.json',{with:{type:'json'}})).default,
  'so':async()=>(await import('./locales/so.json',{with:{type:'json'}})).default,
  'ro':async()=>(await import('./locales/ro.json',{with:{type:'json'}})).default,
  'sq':async()=>(await import('./locales/sq.json',{with:{type:'json'}})).default,
  'nl':async()=>(await import('./locales/nl.json',{with:{type:'json'}})).default,
  'pt-br':async()=>(await import('./locales/pt-br.json',{with:{type:'json'}})).default,
  'pl':async()=>(await import('./locales/pl.json',{with:{type:'json'}})).default,
  'ko':async()=>(await import('./locales/ko.json',{with:{type:'json'}})).default,
  'uk':async()=>(await import('./locales/uk.json',{with:{type:'json'}})).default,
  'hi-IN':async()=>(await import('./locales/hi-IN.json',{with:{type:'json'}})).default,
  'fr':async()=>(await import('./locales/fr.json',{with:{type:'json'}})).default,
  'zh-CN':async()=>(await import('./locales/zh-CN.json',{with:{type:'json'}})).default,
};
const listeners=new Set();

export function resource(messages){
  return new FluentResource(Object.entries(messages).map(([id,value])=>`${id} = ${value.replaceAll('\n','\n    ')}`).join('\n'));
}
export function createLocalizer(locale,messages,fallback=english){
  function bundle(language,catalog){
    const parsed=resource(catalog);
    if(parsed.body.length!==Object.keys(catalog).length)throw Error(`Invalid Fluent catalog: ${language}`);
    const result=new FluentBundle(language,{useIsolating:false});
    const errors=result.addResource(parsed);
    if(errors.length)throw errors[0];
    return result;
  }
  const primary=bundle(locale,messages),backup=bundle('en',fallback);
  return (id,args)=>{
    for(const source of [primary,backup]){
      const message=source.getMessage(id);
      if(!message?.value)continue;
      const errors=[];
      const value=source.formatPattern(message.value,args,errors);
      if(!errors.length)return value;
    }
    return id;
  };
}
export function matchLanguage(preferences){
  for(const value of preferences){
    try{
      const canonical=Intl.getCanonicalLocales(value)[0].toLowerCase();
      const locale=new Intl.Locale(canonical).maximize();
      if(locale.language==='zh'){
        if(locale.script==='Hans')return 'zh-CN';
        continue;
      }
      const parts=canonical.split('-');
      while(parts.length){
        const match=languages.find(item=>item.code.toLowerCase()===parts.join('-'));
        if(match)return match.code;
        if(parts.length===1){
          const regional=languages.filter(item=>item.code.toLowerCase().split('-')[0]===parts[0]);
          if(regional.length===1)return regional[0].code;
        }
        parts.pop();
      }
    }catch{}
  }
  return 'en';
}
let current='en',format=createLocalizer('en',english),generation=0,stateCache;
export const t=(id,args)=>format(id,args);
export const escapeUi=value=>String(value).replace(/[&<>"']/g,char=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[char]));
export const htmlMessage=(id,args)=>escapeUi(t(id,args));
export const onLanguageChange=callback=>{listeners.add(callback);return()=>listeners.delete(callback);};
export function localeState(){
  if(stateCache)return stateCache;
  const language=languages.find(item=>item.code===current);
  // Parameterized messages are formatted beside their owning state event.
  const messages=Object.fromEntries(Object.keys(english).filter(id=>!english[id].includes('$')).map(id=>[id,t(id)]));
  messages['locale-language']=current;
  messages['locale-direction']=language.direction;
  for(const [id,args]of Object.entries({
    'language-suggestion':{language:'{language}'},
    'appearance-theme-name':{theme:'{theme}'},
    'audio-time-description':{position:'{position}',duration:'{duration}'},
    'book-library-kind':{kind:'{kind}'},
    'book-current-kind':{kind:'{kind}'},
  }))messages[id]=t(id,args);
  for(const rate of ['0.5','0.75','1','1.25','1.5','1.75','2','2.5','3'])messages['rate-'+rate]=t('audio-rate-value',{rate:Number(rate)});
  stateCache={language:current,direction:language.direction,languages,messages};
  return stateCache;
}
export async function setLanguage(language,{persist=true}={}){
  if(!languages.some(item=>item.code===language))throw Error('Unsupported interface language');
  const request=++generation;
  const catalog=await loaders[language]();
  const next=createLocalizer(language,catalog);
  if(request!==generation)return false;
  current=language;format=next;stateCache=undefined;
  if(persist)try{localStorage.setItem(languageStorageKey,language);}catch{}
  if(typeof document!=='undefined'){
    if(language==='zh-CN'){
      const href=new URL('fonts/reading/noto-sans-sc/index.css',document.baseURI).href;
      if(![...document.querySelectorAll('link[rel="stylesheet"]')].some(link=>link.href===href)){
        const link=document.createElement('link');link.rel='stylesheet';link.href=href;document.head.append(link);
      }
    }
    document.documentElement.lang=language;
    document.documentElement.dir=languages.find(item=>item.code===language).direction;
  }
  for(const listener of listeners)listener(localeState());
  return true;
}
export async function initializeLanguage(){
  let saved;
  try{saved=localStorage.getItem(languageStorageKey);}catch{}
  const selected=matchLanguage(saved?[saved]:['en']);
  try{await setLanguage(selected,{persist:false});}
  catch{await setLanguage('en',{persist:false});}
}

// Only these messages depend on Elm state. Formatting them here keeps Fluent's
// plural/number rules shared with JavaScript, without synchronous JS/Elm hacks.
const context={};
export function elmLabels(tag,data){
  context[tag]=data;
  const labels={};
  if(tag==='progress'||tag==='book'||tag==='locale'){
    const progress=context.progress??{percent:0,remaining:0};
    labels['reading-note']=t(context.book?.kind==='pdf'?'reading-document-progress':'reading-chapter-progress',{percent:Math.round(progress.percent)});
    if(progress.remaining>0)labels['reading-note']+=' · '+t('reading-time-left',{count:progress.remaining});
  }
  if(tag==='toc'||tag==='book'||tag==='locale')labels['chapter-count']=t(context.book?.kind==='pdf'?'book-page-count':'book-chapter-count',{count:context.toc?.length??0});
  if(tag==='library'||tag==='locale')labels['language-count']=t('book-language-count',{count:(context.library??[]).filter(book=>book.id.startsWith('easypeasy-')).length});
  if(tag==='audio'||tag==='locale'){
    const audio=context.audio??{rate:1,position:0,duration:0,totalPosition:0,totalDuration:0};
    const time=value=>{const seconds=Math.max(0,Math.floor(value??0));return (seconds>=3600?Math.floor(seconds/3600)+':':'')+String(Math.floor(seconds/60)%60).padStart(seconds>=3600?2:1,'0')+':'+String(seconds%60).padStart(2,'0');};
    labels['audio-time']=t('audio-time-description',{position:time(audio.position),duration:time(audio.duration)});
    labels['audio-book-time']=t('audio-time-description',{position:time(audio.totalPosition),duration:time(audio.totalDuration)});
    labels['audio-speed']=t('audio-speed-description',{rate:audio.rate});
    labels['audio-rate']=t('audio-rate-value',{rate:audio.rate});
  }
  return labels;
}
